/**
 * Types for the backend contract.
 *
 * The three at the top mirror `/openapi.json` on cast-asst-backend exactly.
 * Once the endpoint list grows, generate this file from the spec rather than
 * maintaining it by hand, so a breaking change is a failing build.
 *
 * The rest describe the domain the MVP screens need. They are not live on the
 * backend yet, which is why the stub data source exists: the screens are built
 * against the shape now and switch to real endpoints without changing.
 */

// ---- live on the backend today ----

export interface Health {
  status: string;
  env: string;
}

export interface Readiness {
  status: string;
  database: string;
}

export interface Me {
  id: string;
  firebase_uid: string;
  email: string | null;
  display_name: string | null;
}

// ---- the MVP domain, not live yet ----

/** What a grid reports about who is up for a role. Not our own submissions. */
export type CandidateStanding =
  | "interest"
  | "suggested"
  | "attached"
  | "set"
  | "out_to"
  | "top_choice"
  | "prototype"
  | "passed"
  | "offer"
  | "appointment"
  | "negotiating"
  | "not_available"
  | "sending_material";

/** Our own pipeline for our own talent. */
export type SubmissionState =
  | "submitted"
  | "audition"
  | "callback"
  | "offer"
  | "booked"
  | "passed";

export interface ProjectAlias {
  alias_type: string;
  value_raw: string;
}

/** What sort of fact a status tag is. See app/lib/statuses.py on the API. */
export type StatusKind = "stage" | "tracking" | "kind" | "desk" | "unknown";

/** One tag out of a production's status cell, as the API reads it. */
export interface StatusTag {
  label: string;
  kind: StatusKind;
}

export interface Project {
  id: string;
  title: string;
  project_type: string | null;
  status_raw: string | null;
  /**
   * The status cell split into its tags, each with what sort of fact it is.
   *
   * The screens used to split status_raw themselves with their own copy of the
   * rule. The API does it once now, so the rule cannot drift between the two,
   * and a badge deciding its colour can ask what a tag is rather than only
   * what it says.
   */
  status_tags: StatusTag[];
  canonical_stage: string | null;
  studio: string | null;
  network: string | null;
  /** The TV networks a production airs on, one name each. The per-network
   *  reading beside `studio`, when the API sends it. */
  networks?: string[];
  production_company: string | null;
  location: string | null;
  /** Backend-native genre values. `genre` below is the display string kept for older screens. */
  genres?: string[];
  genre: string | null;
  aliases?: ProjectAlias[];
  budget: string | null;
  synopsis: string | null;
  source_agency: string;
  /** Shown on the dashboard's tracking rows as a date range. */
  start_date: string | null;
  wrap_date: string | null;
  /**
   * The sortable shoot date, ISO. The key to order roles by when they shoot:
   * set even where `start_date` is null because the grid gave a month or a
   * quarter rather than a day, so those still sort in the right place instead
   * of falling to the bottom. Optional for an API from before it existed.
   */
  start_date_sort?: string | null;
  /**
   * How precise that shoot date is: "day", "month", "quarter", "season" or
   * "year", null when the grid gave no year. `start_date_sort` is always a full
   * day because it has to sort, so this says how much of it to trust. Optional
   * for an API from before it existed. Carried through now so a screen that
   * wants to label a quarter as a quarter has it; the shooting cell still reads
   * the grid's own phrasing from `start_date_as_written`.
   */
  start_date_precision?: string | null;
  /**
   * What the grid wrote where it was not a date.
   *
   * "Mid-August 2026", "End Of Year 2026", "TBD". True, useful to somebody
   * planning round an availability, and not a day, so the date above stays
   * null and the words come through here. Never both filled for one field: a
   * value is either a date or it is words.
   */
  start_date_as_written: string | null;
  wrap_date_as_written: string | null;
  /** Key crew and cast, listed on the tracking row. */
  key_people: string[];
  /** The same people split by the capacity the source attached them in.
   *
   *  Lists rather than single values: a production routinely carries several
   *  producers, and the IE grids have Producer, Executive Producer and
   *  Co-Producer as separate columns which all land here. A name that both
   *  directs and produces appears in both, so these are two views onto
   *  key_people rather than a split of it. */
  directors: string[];
  producers: string[];
  casting_associates?: string[];
  casting_assistants?: string[];
  production_executives?: string[];
  /** The same people with a way to reach them, shown on project details.
   *  Optional because not every grid record carries contact details. */
  contacts?: Contact[];
}

/** Someone attached to a production, and how to reach them. */
export interface Contact {
  name: string;
  role: string;
  phone: string | null;
  email: string | null;
}

/** One call or email logged against a project.
 *
 *  There is no table for this yet and it is not in any of the three MVP user
 *  stories, so entries added here live for the session only. The screen has
 *  to show the shape because logging a call after making one is the thing
 *  managers said they do most. */
export interface CallLogEntry {
  id: string;
  project_id: string;
  occurred_at: string;
  contact: string;
  kind: "Phone" | "Email";
  notes: string;
  follow_up: string | null;
}

export interface Role {
  id: string;
  project_id: string;
  name: string;
  role_type: string | null;
  /** The controlled billing key behind role_type ("series_regular", "guest_star"),
   *  when the grid's wording mapped to one. role_type is what a grid printed;
   *  this is the agreed vocabulary, which is what a tier sort can rely on. */
  role_type_key: string | null;
  description: string | null;
  gender: string | null;
  age_min: number | null;
  age_max: number | null;
  ethnicities: string[];
  is_open: boolean;
}

/** A role plus the project it belongs to, which is how screens display it. */
export interface RoleWithProject extends Role {
  project: Project;
  candidate_count: number;
  /** Actors the grids list for this exact role, and the strongest standing
   *  among them. Per role, unlike candidate_count which is the production's
   *  total. Used to flag in the role list that a part has real names on it,
   *  and whether it is effectively cast. Optional so the fixtures that predate
   *  them still type; the live API always sends them. */
  actors_mentioned?: number;
  top_standing?: string | null;
  submission_state: SubmissionState | null;
  /** When this role first matched. No longer a column on the match list: 1v1
   *  called it duplicative on a list where every row matched. Still what the
   *  tracking tab sorts and reports on.
   *
   *  Nullable, and usually null. It is the production's start date, and the
   *  grids mostly do not carry one. Anything reading it has to handle that:
   *  new Date(null) is the epoch, not an absence. */
  matched_at: string | null;
  /**
   * Whether the manager asking has read this production for this client.
   *
   * On the production, not the role. A production with eight parts on a
   * client's list is one thing to read, and clearing it row by row would be
   * the screen inventing work.
   *
   * Null where there is no client in the question, which is the roles
   * directory. Read belongs to a client's list, so there is no answer to give
   * there rather than a negative one.
   */
  seen: boolean | null;
}

export interface MatchedRolesPage {
  items: RoleWithProject[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
  capped: boolean;
  new_count: number;
}

export interface RolesPage {
  items: RoleWithProject[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface RoleOptions {
  role_types: string[];
  /**
   * The controlled role-type keys the grids resolve to, lead through co_star,
   * the vocabulary Paul approved. role_types above is the grid's own wording;
   * this is what the Role Type multi-select filters on, so one key selects
   * every part billed that way however the grid spelled it.
   */
  role_type_keys: string[];
  project_types: string[];
  genders: string[];
  locations: string[];
  /** The casting stage tags, which is what a picker labelled Status means. */
  statuses: string[];
  /**
   * Every tag the grids put in the status cell, with what sort of thing it is.
   *
   * The cell is a comma joined list of tags and they are four different kinds
   * of fact: where the production is in casting, which of 1v1's lists it is
   * on, what kind of production it is, and the agency's own desk codes. One
   * picker offering all four under a heading reading Status is why nobody
   * trusted it, so the screen groups them.
   *
   * kind is one of stage, tracking, kind, desk, unknown. unknown is a tag this
   * build of the API has never seen, which is a prompt to go and ask rather
   * than something to hide.
   */
  status_options: StatusOption[];
}

export interface ListRolesOptions {
  limit?: number;
  offset?: number;
  q?: string;
  role_type?: string;
  /**
   * The controlled role-type keys to filter on, any of which matches. The
   * directory's Role Type pill sends these; role_type above is the older
   * single free-text match the saved client criteria still use.
   */
  role_type_key?: string[];
  project_type?: string;
  gender?: string;
  location?: string;
  status?: string;
  added_within_days?: number;
}

export interface FollowedRoleClient {
  client_id: string;
  name: string;
}

export interface FollowedRole {
  role_id: string;
  clients: FollowedRoleClient[];
}

export interface ClientFilters {
  /** Fields taken from the Add Client screen in the design. */
  // One size of part or several: 1v1 asked to pick more than one at once. A
  // string is still accepted, so a filter saved before multi-select still
  // reads, and the backend returns it in whatever shape it was stored.
  role_type: string | string[] | null;
  gender: string | null;
  age_min: number | null;
  age_max: number | null;
  preferred_genres: string[];
  film_tv_preference: string | null;
  // No location_market. It was in the shape, no form ever collected it, and
  // the match query never read it. Paul said on the call they do not search by
  // location, so it came off the API and the stored key was cleared with it.
}

export interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  agency: string | null;
  representation_type: string | null;
  ethnicity: string | null;
  avatar_url: string | null;
  filters: ClientFilters;
  created_at: string;
}

export type NewClient = Omit<Client, "id" | "created_at">;

export const EMPTY_FILTERS: ClientFilters = {
  role_type: null,
  gender: null,
  age_min: null,
  age_max: null,
  preferred_genres: [],
  film_tv_preference: null,
};

/** Full name, since the design splits first and last but shows them joined. */
export function clientName(c: Client): string {
  return `${c.first_name} ${c.last_name}`.trim();
}

/** One entry in a project's activity feed. */
export interface UpdateClient {
  client_id: string;
  name: string;
  /**
   * Whether this manager has read the production for this client.
   *
   * The update's own seen is the whole row's answer and is right for the
   * ungrouped list. Grouped by client, unread has to mean unread for the
   * client whose heading it is under, which is this.
   */
  seen: boolean;
}

export interface StatusOption {
  label: string;
  kind: StatusKind;
  productions: number;
}

export interface ProjectUpdate {
  id: string;
  project_id: string;
  /**
   * Whose update this is: the clients somebody is following a part on this
   * production for.
   *
   * A list, because one production can carry parts starred for more than one
   * client. Empty means nobody is following anything on it, which is not the
   * same as it being nobody's problem, so the row still shows.
   */
  clients: UpdateClient[];
  body: string;
  /** The second line under the headline, e.g. "Project Artemis • Apple Studios". */
  detail: string;
  kind: string;
  occurred_at: string;
  /**
   * Whether this manager has read the production the update is about.
   *
   * The same read state the match list keeps, per manager per client per
   * production, not a second one. Marking a production read on a client's
   * list clears its updates here too.
   *
   * null where nobody is following a part on the production. Those rows still
   * show, but read is keyed on a client and there is not one, so they are
   * drawn as neither. False would make them unread forever on a screen with
   * no way to read them.
   */
  seen: boolean | null;
}

/** One production an assistant answer was built from. */
export interface ChatCitation {
  kind: string;
  id: string;
  label: string;
  detail: string | null;
}

/** One tool the assistant called while answering, as the log recorded it. */
export interface LoggedToolCall {
  tool: string;
  /** Parsed where the model sent valid JSON, the raw text where it did not. */
  arguments: unknown;
  /** What the tool returned. null on turns from before results were kept. */
  result: string | null;
}

/** One question to the assistant and what it did to answer it. */
export interface AssistantTurn {
  id: string;
  asked_at: string;
  asked_by: string | null;
  question: string;
  reply: string;
  tools: LoggedToolCall[];
  citations: ChatCitation[];
  model: string | null;
  rounds: number;
  wrote: boolean;
}

export interface AssistantTurnsPage {
  items: AssistantTurn[];
  total_count: number;
  limit: number;
  offset: number;
}

/** Somebody on a production whose name is in an Outlook address book. */
export interface KnownContact {
  person: string;
  capacities: string[];
  /** How the contact was written in the address book. */
  contact_name: string;
  mailbox: string;
  /** exact, or nickname when only the first name differs by a short form. */
  match: "exact" | "nickname";
  /** Every address and number the address book holds for that name. */
  emails?: string[];
  phones?: { kind: string; number: string }[];
}

/** An actor the grids list against a role, and where they stand on it. */
export interface RoleCandidate {
  name: string;
  /** Normalised standing, for grouping (attached, suggested, offer, ...). */
  standing: string;
  /** What to show: the grid's own wording where it gave one. */
  standing_label: string;
}

/** A call the assistant would write, waiting on somebody to say yes. */
export interface ProposedCall {
  project_id: string;
  project_title: string;
  contact: string;
  channel: string;
  notes: string;
  follow_up: string | null;
}

/** One field a proposed change would set, shown before and after. */
export interface ProposedFieldChange {
  /** The label the screen draws, e.g. "Studio" or "Start date". */
  field: string;
  /** What is on the record now, or null where the field was empty. A null is
   *  drawn as "not set" rather than left blank, so a fill reads as a fill and
   *  not as a change from nothing to nothing. */
  before: string | null;
  /** What the change would set it to. */
  after: string;
}

/** A role a proposed change would add to a production. */
export interface ProposedRole {
  name: string;
  /** Where the part stands if the text said, e.g. "offer", otherwise null. */
  standing: string | null;
}

/**
 * One production a review could be meaning, offered for the manager to pick.
 *
 * A review is a likely match, not a sure one, so the proposal carries the near
 * productions rather than committing to the top guess. Each has its own diff
 * against the paste, so picking a different one redraws the change with no round
 * trip: the before on its fields is that production's value, not the top one's.
 */
export interface ProposedCandidate {
  project_id: string;
  project_title: string;
  /** How strongly it matched, best first in the list. */
  score: number;
  fields: ProposedFieldChange[];
}

/**
 * A change the assistant would make to the record, waiting on a yes.
 *
 * The same idea as a proposed call: shown in full before anything is written,
 * because a confirmed change writes to a production and tells everyone
 * following it, and there is no taking it back from the panel.
 *
 * kind says what the yes would do:
 *   new     the pasted production is not one we hold, so this creates it
 *   update  it matched a production we hold, so this amends that one
 *   review  it matched one but not surely, so the title is named for the
 *           manager to confirm is the right production before anything is set
 */
export interface ProposedChange {
  kind: "new" | "update" | "review";
  /** The production the change is against. Null for a brand new one. */
  project_id: string | null;
  /** The title as it would read: the pasted one for new, the matched one else. */
  project_title: string;
  fields: ProposedFieldChange[];
  roles: ProposedRole[];
  /** Only on a review: the productions the paste could be, best first, so the
   *  manager picks one rather than taking the top guess. Empty otherwise. */
  alternatives: ProposedCandidate[];
  /** The text the change was read from, sent back on apply so the record logs
   *  what each change came from. Null when there is no paste behind it. */
  source_text: string | null;
}

export interface ChatTurnIn {
  role: "user" | "assistant";
  content: string;
}

export interface ChatAnswer {
  reply: string;
  citations: ChatCitation[];
  used: string[];
  proposed_call: ProposedCall | null;
}

export interface ChatStatus {
  connected: boolean;
  model: string | null;
}

/** The agencies a grid can be filed under, matching the backend enum. */
export const GRID_AGENCIES = ["wme", "ie", "other"] as const;
export type GridAgency = (typeof GRID_AGENCIES)[number];

/**
 * The document a grid upload created or matched.
 *
 * duplicate is true when the exact file was already uploaded, in which case
 * the backend wrote nothing and returned the document already on record.
 */
export interface GridUpload {
  document_id: string;
  filename: string;
  source_agency: string;
  status: string;
  storage_key: string;
  duplicate: boolean;
}
